import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { StatementHistoriesParams, StatementHistory } from '@/interfaces/statement';
import statementsService from '@/api/statementsService';

type StatementHistoriesFilters = StatementHistoriesParams & { page: number };

interface MetaData {
  totalPages: number;
  totalElements: number;
}

interface StatementsState {
  statements: { [key: string]: StatementHistory[] };
  loading: boolean;
  error: string;
  currFilters: string;
  metaData: { [key: string]: MetaData };
  files: { [key: string]: string };
  fileLoading: boolean;
}

const initialState: StatementsState = {
  statements: {},
  loading: false,
  error: '',
  currFilters: '',
  metaData: {},
  files: {},
  fileLoading: false,
};

export const fetchStatementHistories = createAsyncThunk(
  'statements/histories',
  async ({ page, ...params }: StatementHistoriesFilters, thunkAPI) => {
    try {
      const result = await statementsService.loadStatementHistories({ ...params, page: page - 1 });
      return result;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.data);
    }
  }
);

export const fetchStatementFile = createAsyncThunk('statements/fetchFile', async (id: string, thunkAPI) => {
  try {
    const result = await statementsService.fetchStatementFile(id);
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.data);
  }
});

export const statementsSlice = createSlice({
  name: 'statements',
  initialState,
  reducers: {
    refresh: () => initialState,
    handleParamsChange: (state, action: PayloadAction<StatementHistoriesFilters>) => {
      state.currFilters = JSON.stringify(action.payload);
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchStatementHistories.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchStatementHistories.fulfilled, (state, action) => {
      const { payload, meta } = action;
      const filters = JSON.stringify(meta.arg);
      state.statements[filters] = payload.data;
      state.metaData[filters] = { totalPages: payload.totalPages, totalElements: payload.totalElements };
      state.loading = false;
    });
    builder.addCase(fetchStatementHistories.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });
    builder.addCase(fetchStatementFile.pending, state => {
      state.fileLoading = true;
    });
    builder.addCase(fetchStatementFile.fulfilled, (state, action) => {
      state.files[action.meta.arg] = action.payload;
      state.fileLoading = false;
    });
    builder.addCase(fetchStatementFile.rejected, state => {
      state.fileLoading = false;
    });
  },
});

export const { handleParamsChange, refresh } = statementsSlice.actions;

export const selectStatements = (state: RootState) => state.statements;

export default statementsSlice.reducer;
